/**
 * FileTabs.tsx — the open-file tab strip above the Monaco editor.
 *
 * One tab per open file. Each tab shows the filename, a dirty indicator (`•`
 * when there are unsaved edits), and a close (X) button. Closing an unsaved
 * tab asks for confirmation via `useConfirm` (house rule: ConfirmDialog, not
 * `window.confirm`). Mirrors the `agent-tab` / `lib-tab` class conventions but
 * uses `code-tab*` classes scoped to the Code panel.
 *
 * Right-click a tab for a context menu: Run… (opens the run-task modal — a
 * notebook or script configured + optionally autostarted), then the standard
 * Save / Rename / Close actions. The run entry is marked "soon" when no run
 * target is available (notebooks + non-runnable kinds).
 */
import { useState, type MouseEvent } from 'react';
import { Play, PencilSimple, FloppyDisk, Plus, X } from '@phosphor-icons/react';
import { useConfirm } from '../panels/ConfirmDialog';

export interface OpenFile {
  /** Project-relative path (also Monaco's model key). */
  path: string;
  /** Bare filename for the tab label. */
  name: string;
  /** True if there are unsaved edits (shows the dirty dot). Absent for tabs
   *  whose state isn't owned here (e.g. a notebook, whose state lives in
   *  Jupyter) — treated as "not dirty". */
  dirty?: boolean;
}

interface FileTabsProps {
  tabs: OpenFile[];
  activePath: string | null;
  onSelect: (path: string) => void;
  /** Close the tab (caller removes it from state + handles editor swap). */
  onClose: (path: string) => void;
  /** Save the tab at path (caller writes its content). */
  onSave: (path: string) => void;
  /** Rename the file at path (opens the rename modal in the parent). */
  onRename: (path: string) => void;
  /** Open context menu to create a new file or sql query. */
  onCreateEntry?: (isSql?: boolean) => void;
}

export function FileTabs({
  tabs,
  activePath,
  onSelect,
  onClose,
  onSave,
  onRename,
  onCreateEntry,
}: FileTabsProps): JSX.Element | null {
  const confirm = useConfirm();
  const [menu, setMenu] = useState<{ x: number; y: number; path: string } | null>(null);
  const [createMenu, setCreateMenu] = useState<{ x: number; y: number } | null>(null);
  
  if (tabs.length === 0 && !onCreateEntry) return null;

  const handleClose = async (file: OpenFile): Promise<void> => {
    if (file.dirty) {
      const ok = await confirm({
        title: `Close ${file.name}?`,
        body: 'This file has unsaved changes that will be lost.',
        confirmLabel: 'Close without saving',
        danger: true,
      });
      if (!ok) return;
    }
    onClose(file.path);
  };

  const openMenu = (e: MouseEvent, path: string): void => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ x: e.clientX, y: e.clientY, path });
  };

  const closeMenu = (): void => setMenu(null);

  const openCreateMenu = (e: MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setCreateMenu({ x: e.clientX, y: e.clientY });
  };

  const closeCreateMenu = (): void => setCreateMenu(null);

  return (
    <div className="code-tabs" role="tablist">
      {tabs.map((file) => {
        const isActive = file.path === activePath;
        return (
          <div
            key={file.path}
            role="tab"
            aria-selected={isActive}
            className={`code-tab${isActive ? ' active' : ''}`}
            onClick={() => onSelect(file.path)}
            onContextMenu={(e) => openMenu(e, file.path)}
          >
            <span className="code-tab-name">
              {file.dirty && <span className="code-tab-dirty" aria-hidden>•</span>}
              {file.name}
            </span>
            <button
              type="button"
              className="code-tab-close"
              aria-label={`Close ${file.name}`}
              onClick={(e) => {
                e.stopPropagation();
                void handleClose(file);
              }}
            >
              <X aria-hidden />
            </button>
          </div>
        );
      })}
      {onCreateEntry && (
        <button
          type="button"
          className="code-tab code-tab-new"
          aria-label="New file"
          title="New file"
          onClick={openCreateMenu}
        >
          <Plus size={14} aria-hidden />
        </button>
      )}
      {menu && (
        <TabContextMenu
          menu={menu}
          onClose={closeMenu}
          onSave={(path) => {
            onSave(path);
            closeMenu();
          }}
          onRename={(path) => {
            onRename(path);
            closeMenu();
          }}
          onCloseTab={(path) => {
            const f = tabs.find((t) => t.path === path);
            if (f) void handleClose(f);
            closeMenu();
          }}
        />
      )}
      {createMenu && onCreateEntry && (
        <CreateMenu
          menu={createMenu}
          onClose={closeCreateMenu}
          onCreate={(sql) => {
            onCreateEntry(sql);
            closeCreateMenu();
          }}
        />
      )}
    </div>
  );
}

/** Only `.py` (run as script/module) and `.ipynb` (run as notebook) are
 *  runnable. SQL/TXT/JSON/etc. have no run semantics yet (SQL execution will be
 *  a separate surface). Used to gate the "Run…" context-menu entry. */
function isRunnable(path: string): boolean {
  return path.endsWith('.py') || path.endsWith('.ipynb');
}

/** Right-click menu on a tab. The Run entry opens the run-task modal (future:
 *  the parent decides what "run" means for this file). Save/Rename/Close act
 *  immediately. Closes on any action, overlay click, or Escape. */
function TabContextMenu({
  menu,
  onClose,
  onSave,
  onRename,
  onCloseTab,
}: {
  menu: { x: number; y: number; path: string };
  onClose: () => void;
  onSave: (path: string) => void;
  onRename: (path: string) => void;
  onCloseTab: (path: string) => void;
}): JSX.Element {
  const runnable = isRunnable(menu.path);
  const itemCount = runnable ? 4 : 3;
  const maxW = 260;
  const estH = itemCount * 30 + (runnable ? 16 : 12);
  const x = Math.min(menu.x, window.innerWidth - maxW - 8);
  const y = Math.min(menu.y, window.innerHeight - estH - 8);

  return (
    <>
      <div
        className="context-overlay"
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <div
        className="context-menu"
        role="menu"
        style={{ left: x, top: y }}
        onClick={(e) => e.stopPropagation()}
      >
        {runnable && (
          <>
            <button
              type="button"
              role="menuitem"
              className="context-item"
              onClick={() => {
                // "Run…" is the entry point for the run-task modal. The actual
                // modal + autostart wiring is surfaced via a custom event so the
                // App shell (which owns the modal) can pick it up without
                // threading a callback through every tab. See App.tsx's
                // RunTaskModal mount.
                window.dispatchEvent(
                  new CustomEvent('code:run-file', { detail: { path: menu.path } }),
                );
                onClose();
              }}
            >
              <span className="context-item-icon">
                <Play aria-hidden />
              </span>
              <span className="context-item-label">Run…</span>
              <span className="context-item-hint">Task</span>
            </button>
            <div className="context-separator" />
          </>
        )}
        <button
          type="button"
          role="menuitem"
          className="context-item"
          onClick={() => onSave(menu.path)}
        >
          <span className="context-item-icon">
            <FloppyDisk aria-hidden />
          </span>
          <span className="context-item-label">Save</span>
        </button>
        <button
          type="button"
          role="menuitem"
          className="context-item"
          onClick={() => onRename(menu.path)}
        >
          <span className="context-item-icon">
            <PencilSimple aria-hidden />
          </span>
          <span className="context-item-label">Rename…</span>
        </button>
        <div className="context-separator" />
        <button
          type="button"
          role="menuitem"
          className="context-item danger"
          onClick={() => onCloseTab(menu.path)}
        >
          <span className="context-item-icon">
            <X aria-hidden />
          </span>
          <span className="context-item-label">Close</span>
        </button>
      </div>
    </>
  );
}

function CreateMenu({
  menu,
  onClose,
  onCreate,
}: {
  menu: { x: number; y: number };
  onClose: () => void;
  onCreate: (sql?: boolean) => void;
}): JSX.Element {
  const maxW = 260;
  const estH = 60;
  const x = Math.min(menu.x, window.innerWidth - maxW - 8);
  const y = Math.min(menu.y, window.innerHeight - estH - 8);

  return (
    <>
      <div
        className="context-overlay"
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <div
        className="context-menu"
        role="menu"
        style={{ left: x, top: y }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          role="menuitem"
          className="context-item"
          onClick={() => onCreate(false)}
        >
          <span className="context-item-label">New file...</span>
        </button>
        <button
          type="button"
          role="menuitem"
          className="context-item"
          onClick={() => onCreate(true)}
        >
          <span className="context-item-label">SQL query</span>
        </button>
      </div>
    </>
  );
}
